<template>
  <div
    :class="{'selected': isSelected, 'has-errors': hasErrors}"
    class="focus-container relative-position"
    @click="focus"
  >
    <slot
      :selected="isSelected"
      :desktop-mode="desktopMode"
    />

    <q-badge
      v-if="hasErrors"
      class="error-badge"
      color="red-7"
      floating
    >
      <q-icon name="mdi-exclamation" />
    </q-badge>

    <portal
      v-if="isSelected"
      :to="`${focusId}-focus-target`"
      slim
    >
      <slot
        name="focus"
        :unfocus="unfocus"
        :desktop-mode="desktopMode"
      />
    </portal>
  </div>
</template>

<script>
import { findParentComponent } from 'src/helpers/globals'

/**
 * FocusContainer defines a focusable section
 *
 * SLOTS:
 * - default (selected, desktopMode) : content to display in container when unfocused
 * - focus (unfocus(), desktopMode) : content to display in sidebar/dialog when the container is being focused on
 */
export default {
  name: 'FocusContainer',
  props: {
    // Unique identifier for this FocusContainer
    linkKey: {
      type: String,
      required: true
    },
    // Title to display in toolbar when focused on
    title: {
      type: String,
      default: null
    },
    // Width of dialog in mobile mode
    dialogWidth: {
      type: String
    },
    // Manually override the default FocusView breakpoints (col-md etc) when this container is focused on
    mainBreakpoint: {
      type: String
    },
    focusBreakpoint: {
      type: String
    },
    // Highlight the container in red if it contains errors
    hasErrors: {
      type: Boolean,
      default: false
    },
    // On desktop mode, perform actions to re-position the focused sidebar content Y position downward (etc)
    focusToScrollPos: {
      type: Function,
      default: null
    },
    // When the container is focused on desktop mode, perform a set of functions
    onDesktopFocus: {
      type: Function,
      default: null
    },
    // When focused, run a function
    onFocus: {
      type: Function,
      default: null
    },
    // Allow container to be re-focused when already active
    allowRefocus: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    focusView () {
      return findParentComponent(this, 'FocusView')
    },
    focusId () {
      return this.focusView.$props.id
    },
    desktopMode () {
      return this.focusView.desktopMode
    },
    isSelected () {
      const currentFocus = this.focusView.$data.currentFocus
      return currentFocus && currentFocus.linkKey === this.linkKey
    }
  },
  watch: {
    '$props': {
      handler () {
        if (this.isSelected) {
          this.focus()
        }
      },
      deep: true
    }
  },
  methods: {
    focus () {
      const top = this.focusToScrollPos ? this.focusToScrollPos(this) : null
      this.$bus.emit(`focus:${this.focusId}`, {
        topPosition: top,
        ...this.$props
      })
    },
    unfocus () {
      if (!this.isSelected) {
        return
      }
      this.$bus.emit(`unfocus:${this.focusId}`, this.linkKey)
    }
  },
  beforeUnmount () {
    // Give the component time for portal to first cleanup and unmount from page before unfocussing
    this.timed(() => {
      this.unfocus()
    })
  }
}
</script>
